import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Plus, Trash2, MessageSquare, Calendar } from "lucide-react";
import { toast } from "sonner";
import { PERSON_TYPES, MEETING_TYPES } from "../constants/propertyOptions";
import MoneyInput from "./MoneyInput";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MeetingNotesTimeline = ({ propertyId, notes = [], onChange, currency = "TRY", currencyOther = "" }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    person: "",
    person_type: "emlakçı",
    meeting_type: "telefon",
    note: "",
    last_price: null,
    offer: null,
    counter_offer: null,
    next_followup_date: "",
    meeting_at: new Date().toISOString().slice(0, 16),
  });

  const resetForm = () => {
    setForm({
      person: "", person_type: "emlakçı", meeting_type: "telefon",
      note: "", last_price: null, offer: null, counter_offer: null,
      next_followup_date: "",
      meeting_at: new Date().toISOString().slice(0, 16),
    });
  };

  const submitNote = async () => {
    if (!propertyId) {
      toast.error("Önce ilanı kaydedin");
      return;
    }
    const payload = { ...form };
    // MoneyInput already emits Number|null — keep as-is, just coerce edge
    // cases (empty string) to null so backend receives clean numeric or null.
    ["last_price", "offer", "counter_offer"].forEach((k) => {
      const v = payload[k];
      payload[k] = (v === "" || v === undefined || v === null || Number.isNaN(v)) ? null : Number(v);
    });
    if (payload.meeting_at) payload.meeting_at = new Date(payload.meeting_at).toISOString();
    if (payload.next_followup_date) payload.next_followup_date = new Date(payload.next_followup_date).toISOString();

    try {
      const { data } = await axios.post(
        `${API_URL}/api/properties/${propertyId}/notes`,
        payload,
        { withCredentials: true }
      );
      onChange?.(data);
      resetForm();
      setAdding(false);
      toast.success("Görüşme notu eklendi");
    } catch (e) {
      console.error(e);
      toast.error("Not eklenemedi");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(
        `${API_URL}/api/properties/${propertyId}/notes/${noteId}`,
        { withCredentials: true }
      );
      onChange?.({ __refresh: true });
      toast.success("Not silindi");
    } catch (e) {
      toast.error("Silme başarısız");
    }
  };

  const sorted = [...notes].sort(
    (a, b) => new Date(b.meeting_at || b.created_at) - new Date(a.meeting_at || a.created_at)
  );

  return (
    <div className="space-y-4">
      {/* Add button */}
      {!adding && (
        <Button
          type="button"
          data-testid="add-meeting-note-button"
          onClick={() => setAdding(true)}
          className="w-full bg-[#0A0A0A] hover:bg-[#18181B] text-white dark:bg-[#FAFAFA] dark:text-[#0A0A0A] dark:hover:bg-[#E4E4E7]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Görüşme Notu Ekle
        </Button>
      )}

      {/* Add form */}
      {adding && (
        <div
          data-testid="meeting-note-form"
          className="p-4 bg-[#F9FAFB] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Tarih & Saat</Label>
              <Input
                type="datetime-local"
                value={form.meeting_at}
                onChange={(e) => setForm({ ...form, meeting_at: e.target.value })}
                className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"
              />
            </div>
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Görüşülen Kişi</Label>
              <Input
                value={form.person}
                onChange={(e) => setForm({ ...form, person: e.target.value })}
                placeholder="Ad Soyad"
                className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Kişi Türü</Label>
              <Select value={form.person_type} onValueChange={(v) => setForm({ ...form, person_type: v })}>
                <SelectTrigger className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent>{PERSON_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Görüşme Şekli</Label>
              <Select value={form.meeting_type} onValueChange={(v) => setForm({ ...form, meeting_type: v })}>
                <SelectTrigger className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent>{MEETING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Görüşme Notu</Label>
            <Textarea
              rows={3}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Emlakçı/ev sahibi ile konuştuklarınız..."
              className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Son Fiyat</Label>
              <MoneyInput
                data-testid="meeting-note-last-price"
                value={form.last_price}
                onChange={(n) => setForm({ ...form, last_price: n })}
                currency={currency}
                currencyOther={currencyOther}
                className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"
              />
            </div>
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Teklifim</Label>
              <MoneyInput
                data-testid="meeting-note-offer"
                value={form.offer}
                onChange={(n) => setForm({ ...form, offer: n })}
                currency={currency}
                currencyOther={currencyOther}
                className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"
              />
            </div>
            <div>
              <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Karşı Teklif</Label>
              <MoneyInput
                data-testid="meeting-note-counter-offer"
                value={form.counter_offer}
                onChange={(n) => setForm({ ...form, counter_offer: n })}
                currency={currency}
                currencyOther={currencyOther}
                className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">Sonraki Takip Tarihi</Label>
            <Input type="date" value={form.next_followup_date}
              onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })}
              className="mt-1 bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]" />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => { setAdding(false); resetForm(); }} className="flex-1">
              Vazgeç
            </Button>
            <Button type="button" data-testid="save-meeting-note-button" onClick={submitNote} className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white">
              Kaydet
            </Button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {sorted.length > 0 ? (
        <div className="space-y-3">
          {sorted.map((n) => (
            <div
              key={n.id}
              data-testid="meeting-note-item"
              className="p-4 bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 text-xs text-[#52525B] dark:text-[#A1A1AA]">
                  <Calendar className="w-3 h-3" />
                  {new Date(n.meeting_at || n.created_at).toLocaleString("tr-TR")}
                  {n.person_type && (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-[#F9FAFB] dark:bg-[#09090B] text-[10px] uppercase tracking-widest font-bold">
                      {n.person_type}
                    </span>
                  )}
                  {n.meeting_type && (
                    <span className="text-[10px] text-[#F59E0B]">• {n.meeting_type}</span>
                  )}
                </div>
                <button
                  onClick={() => deleteNote(n.id)}
                  className="text-[#52525B] hover:text-red-600 dark:text-[#A1A1AA]"
                  aria-label="Sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {n.person && (
                <div className="text-sm font-semibold text-[#0A0A0A] dark:text-[#FAFAFA] mb-1">
                  {n.person}
                </div>
              )}
              {n.note && (
                <p className="text-sm text-[#0A0A0A] dark:text-[#FAFAFA] leading-relaxed whitespace-pre-wrap">
                  {n.note}
                </p>
              )}
              {(n.last_price || n.offer || n.counter_offer) && (
                <div className="mt-3 pt-3 border-t border-[#E4E4E7] dark:border-[#27272A] grid grid-cols-3 gap-2 text-xs">
                  {n.last_price != null && (
                    <div>
                      <div className="text-[#52525B] dark:text-[#A1A1AA] uppercase text-[10px] tracking-widest">Son Fiyat</div>
                      <div className="font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]">{Number(n.last_price).toLocaleString("tr-TR")} ₺</div>
                    </div>
                  )}
                  {n.offer != null && (
                    <div>
                      <div className="text-[#52525B] dark:text-[#A1A1AA] uppercase text-[10px] tracking-widest">Teklifim</div>
                      <div className="font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]">{Number(n.offer).toLocaleString("tr-TR")} ₺</div>
                    </div>
                  )}
                  {n.counter_offer != null && (
                    <div>
                      <div className="text-[#52525B] dark:text-[#A1A1AA] uppercase text-[10px] tracking-widest">Karşı</div>
                      <div className="font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]">{Number(n.counter_offer).toLocaleString("tr-TR")} ₺</div>
                    </div>
                  )}
                </div>
              )}
              {n.next_followup_date && (
                <div className="mt-2 text-xs text-[#F59E0B] font-semibold">
                  ⏰ Sonraki takip: {new Date(n.next_followup_date).toLocaleDateString("tr-TR")}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-xs text-[#52525B] dark:text-[#A1A1AA]">
          <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />
          Henüz görüşme notu eklenmemiş.
        </div>
      )}
    </div>
  );
};

export default MeetingNotesTimeline;
